import React from 'react';
import { Alert, Button, Badge } from '@parsina/framework';
import { Coins } from 'lucide-react';

import { C, CT, soft } from '../../constants/theme';
import { fmtParseh, canAfford } from '../../constants/pricing';
import { useTranslation } from '../../i18n/I18nProvider.jsx';

// What an AI action costs, said BEFORE it is pressed — and what to do when the
// student cannot pay for it.
//
// ── The price is shown before the spend, never after ──────────────────────
//
// Every AI action in this app debits a student's own wallet, and a student is
// exactly the person for whom ten پارسه is a number worth knowing in advance.
// So the cost rides ON the control that spends it, not in a receipt afterwards
// and not on the credits screen where nobody is standing when they press «بپرس».
//
// The numbers come from src/constants/pricing.js — ONE source for the chip
// beside a button, the price list on the credits screen and the "can they afford
// it?" guard — so the advertised price and the shown price cannot disagree.
//
// ── They are ESTIMATES, and the file that owns them says why ─────────────
//
// The deduction happens inside the AI microservice after the BFF has handed off,
// and that service's credit check FAILS OPEN: when the wallet is unreachable
// `get_balance` returns -1.0 and the request is allowed through UNCHARGED
// (CORE.md §8, hard rule 15). So an action can legitimately complete having cost
// nothing. That is correct behaviour — an outage must not lock a student out
// mid-study — and it is why nothing here ever SUMS these to produce a balance.
// `myWalletBalance` is the only authority.

/**
 * «۱۰ پارسه» — the price chip that rides beside a spending control.
 *
 * A cost of zero renders «رایگان» rather than «۰ پارسه»: ورود صوتی bills nothing
 * TODAY (the RPC behind it calls neither the credit check nor the deduction), and
 * a free action a student does not know is free is one they will not use. When
 * the service starts charging, the number in pricing.js changes and this follows.
 */
export function CostChip({ cost, before = false, style }) {
  const { t, formatNumber } = useTranslation();

  if (!Number(cost)) {
    return (
      <Badge variant="success" style={style} data-testid="cost-chip">
        {t('assistant.costFree')}
      </Badge>
    );
  }

  const parseh = fmtParseh(cost, formatNumber);
  return (
    <span
      data-testid="cost-chip"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 11.5,
        fontWeight: 700,
        color: CT.accentInk,
        background: soft(C.teal, 0.14),
        borderRadius: 999,
        padding: '3px 10px',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <Coins size={12} aria-hidden />
      {t(before ? 'assistant.costBefore' : 'assistant.cost', { parseh })}
    </span>
  );
}

/**
 * «موجودی شما ۳۲۰ پارسه است» — the standing balance line on the assistant.
 *
 * Rendered near the controls that spend it rather than only in the rail, because
 * the decision the student is making is «آیا این را بپرسم؟» and the two numbers
 * that answer it should be within one glance of each other.
 *
 * A null balance renders nothing at all instead of «—»: the balance query is in
 * flight, and a dash beside a spend button reads as "you have none".
 */
export function BalanceLine({ balance, style }) {
  const { t, formatNumber } = useTranslation();
  if (balance == null) return null;
  return (
    <span
      data-testid="balance-line"
      style={{ fontSize: 12, color: CT.ink2, display: 'inline-flex', alignItems: 'center', gap: 5, ...style }}
    >
      <Coins size={13} color={C.teal} aria-hidden />
      {t('assistant.balance', { parseh: fmtParseh(balance, formatNumber) })}
    </span>
  );
}

/**
 * What the screen shows when the AI refused for lack of پارسه.
 *
 * The BFF re-raises the AI service's gRPC RESOURCE_EXHAUSTED as
 * INSUFFICIENT_CREDITS, and `isOutOfCredits(err)` in src/lib/bffClient.js is the
 * predicate three screens branch on. The recovery is «شارژ پارسه» — never a
 * retry, which cannot succeed because the balance has not moved — and the copy
 * says the student's work is intact, because the mutation wrote nothing: a failed
 * AI call bills nothing and leaves no unanswered question in the transcript.
 */
export function OutOfCredits({ onTopUp, style }) {
  const { t } = useTranslation();
  return (
    <Alert
      variant="error"
      title={t('assistant.noCredits.title')}
      icon={<Coins size={18} aria-hidden />}
      action={
        <Button size="sm" onClick={onTopUp}>
          {t('assistant.noCredits.action')}
        </Button>
      }
      data-testid="out-of-credits"
      style={style}
    >
      {t('assistant.noCredits.body')}
    </Alert>
  );
}

/**
 * Can this balance pay for that action?
 *
 * Re-exported here so a screen imports the guard from the same module as the
 * chip that advertises the price. A balance that has not loaded yet ALLOWS the
 * action: blocking a student who has plenty because a query is still in flight
 * would be a worse failure than the round trip, and the server is the real gate
 * anyway.
 */
export { canAfford };

export default CostChip;
